/**
 * Volume Normalization Service
 * Analyzes and normalizes audio levels for consistent playback
 */
class VolumeNormalization {
  constructor() {
    this.enabled = false;
    this.targetLevel = -16; // Target LUFS (Loudness Units Full Scale)
    this.audioCache = new Map(); // Cache analyzed audio data
    this.analysisCallbacks = new Map();
  }

  /**
   * Enable or disable volume normalization
   */
  setEnabled(enabled) {
    this.enabled = enabled;
  }

  /**
   * Analyze audio file and calculate gain adjustment
   */
  async analyzeAudio(audioElement) {
    if (!this.enabled) return 1.0;

    const src = audioElement.src || audioElement.currentSrc;
    if (!src) return 1.0;

    // Check cache first
    if (this.audioCache.has(src)) {
      return this.audioCache.get(src);
    }

    try {
      // Create audio context for analysis
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaElementSource(audioElement);
      const analyzer = audioContext.createAnalyser();

      analyzer.fftSize = 256;
      source.connect(analyzer);

      // Analyze audio levels
      const gainAdjustment = await this.calculateGainAdjustment(analyzer, audioContext);

      // Cache the result
      this.audioCache.set(src, gainAdjustment);

      // Cleanup
      source.disconnect();
      await audioContext.close();

      return gainAdjustment;
    } catch (error) {
      console.error('Error analyzing audio:', error);
      return 1.0;
    }
  }

  /**
   * Calculate gain adjustment based on audio analysis
   */
  async calculateGainAdjustment(analyzer, audioContext) {
    return new Promise((resolve) => {
      const bufferLength = analyzer.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      // Sample audio data
      let totalVolume = 0;
      let sampleCount = 0;

      const sampleAudio = () => {
        analyzer.getByteFrequencyData(dataArray);

        // Calculate RMS (Root Mean Square) volume
        let sumSquares = 0;
        for (let i = 0; i < bufferLength; i++) {
          const value = dataArray[i] / 255.0; // Normalize to 0-1
          sumSquares += value * value;
        }

        const rms = Math.sqrt(sumSquares / bufferLength);
        totalVolume += rms;
        sampleCount++;

        // Sample for 1 second
        if (sampleCount < 60) {
          requestAnimationFrame(sampleAudio);
        } else {
          // Calculate average volume and gain adjustment
          const averageVolume = totalVolume / sampleCount;
          const currentLUFS = this.rmsToLUFS(averageVolume);
          const gainAdjustment = this.calculateGain(currentLUFS);

          resolve(gainAdjustment);
        }
      };

      sampleAudio();
    });
  }

  /**
   * Convert RMS to LUFS (simplified)
   */
  rmsToLUFS(rms) {
    // Simplified conversion from RMS to LUFS
    // Real LUFS calculation is more complex
    return -20 * Math.log10(rms + 0.0001);
  }

  /**
   * Calculate gain adjustment needed
   */
  calculateGain(currentLUFS) {
    const difference = this.targetLevel - currentLUFS;
    const gainAdjustment = Math.pow(10, difference / 20);

    // Limit gain adjustment to prevent extreme changes
    return Math.max(0.5, Math.min(2.0, gainAdjustment));
  }

  /**
   * Apply gain adjustment to audio element
   */
  applyGainAdjustment(audioElement, gainAdjustment) {
    if (!this.enabled || gainAdjustment === 1.0) return;

    // Apply gain using volume property
    const currentVolume = audioElement.volume;
    audioElement.volume = Math.min(1.0, currentVolume * gainAdjustment);
  }

  /**
   * Reset normalization cache
   */
  resetCache() {
    this.audioCache.clear();
  }

  /**
   * Get cached gain adjustment for audio source
   */
  getCachedGain(src) {
    return this.audioCache.get(src) || 1.0;
  }

  /**
   * Pre-analyze audio file for better performance
   */
  async preAnalyzeAudio(audioUrl) {
    if (!this.enabled || this.audioCache.has(audioUrl)) return;

    try {
      const audio = new Audio();
      audio.crossOrigin = 'anonymous';
      audio.src = audioUrl;

      await new Promise((resolve, reject) => {
        audio.addEventListener('canplaythrough', resolve, { once: true });
        audio.addEventListener('error', reject, { once: true });
        audio.load();
      });

      const gainAdjustment = await this.analyzeAudio(audio);
      this.audioCache.set(audioUrl, gainAdjustment);

      return gainAdjustment;
    } catch (error) {
      console.error('Error pre-analyzing audio:', error);
      return 1.0;
    }
  }
}

// Export singleton instance
export const volumeNormalization = new VolumeNormalization();
export default volumeNormalization;